import React, { useState, useEffect, useMemo } from 'react';
import { marketplaceAPI } from '../../../services/marketplaceAPI';
import ProductCard from '../../../components/marketplace/ProductCard';
import { Search, ShoppingBag, X, Filter, Clock, Heart, SlidersHorizontal, MapPin } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import ProductDetailsModal from '../../../components/marketplace/ProductDetailsModal';
import CheckoutModal from '../../../components/marketplace/CheckoutModal';
import OrderHistoryModal from '../../../components/marketplace/OrderHistoryModal';
import WishlistModal from '../../../components/marketplace/WishlistModal';
import ProviderStoreModal from '../../../components/marketplace/ProviderStoreModal';
import '../../../styles/marketplace.css';

const GuardianMarketplace = ({
    user,
    cartItems,
    isCartOpen,
    setIsCartOpen,
    onAddToCart,
    onRemoveFromCart,
    onUpdateQuantity
}) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest'); // 'newest', 'price-low', 'price-high', 'rating'
    const [priceRange, setPriceRange] = useState({ min: '', max: '' });
    const [categories, setCategories] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [currency, setCurrency] = useState(() => localStorage.getItem('marketplace_currency') || 'SAR');
    const [exchangeRates, setExchangeRates] = useState({});
    const [wishlistItems, setWishlistItems] = useState(() => {
        const saved = localStorage.getItem('marketplace_wishlist');
        return saved ? JSON.parse(saved) : [];
    });

    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
    const [isOrderHistoryOpen, setIsOrderHistoryOpen] = useState(false);
    const [isWishlistOpen, setIsWishlistOpen] = useState(false);

    // City Filtering State
    const [availableCities, setAvailableCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState('all');

    useEffect(() => {
        localStorage.setItem('marketplace_currency', currency);
    }, [currency]);

    useEffect(() => {
        localStorage.setItem('marketplace_wishlist', JSON.stringify(wishlistItems));
    }, [wishlistItems]);

    // ... (rest of useEffects)

    const toggleWishlist = (product) => {
        setWishlistItems(prev => {
            const exists = prev.find(item => item.id === product.id);
            if (exists) {
                return prev.filter(item => item.id !== product.id);
            } else {
                return [...prev, product];
            }
        });
    };

    // Ensure state is valid before rendering:
    // console.log("isWishlistOpen:", isWishlistOpen); 

    const removeFromWishlist = (id) => {
        setWishlistItems(prev => prev.filter(item => item.id !== id));
    };

    // ... (rest of component: fetchProducts, handleOrderPlaced)



    // Supported countries for mock data
    const SUPPORTED_COUNTRIES = ['Saudi Arabia', 'United Arab Emirates', 'Kuwait'];

    // Determine effective country (fallback to Saudi Arabia if user's country is not supported in mock data)
    const effectiveCountry = useMemo(() => {
        const userCountry = user?.country;
        return (userCountry && SUPPORTED_COUNTRIES.includes(userCountry))
            ? userCountry
            : 'Saudi Arabia';
    }, [user?.country]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const cats = marketplaceAPI.getCategories();
                setCategories(cats);

                // Load cities for effective country
                const cities = marketplaceAPI.getCities(effectiveCountry);
                setAvailableCities(cities);

                const rateData = await marketplaceAPI.getExchangeRates();
                if (rateData.status === 'success') {
                    setExchangeRates(rateData.rates);
                    // Only set base currency if user hasn't selected one (or if it's the very first load and no storage)
                    if (!localStorage.getItem('marketplace_currency')) {
                        setCurrency(rateData.base);
                    }
                }

                await fetchProducts();
            } catch (error) {
                console.error("Failed to load marketplace:", error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [user, effectiveCountry]); // Reload if user or effective country changes

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await marketplaceAPI.getProducts({
                category: activeCategory,
                search: searchQuery,
                country: effectiveCountry,
                city: selectedCity
            });
            if (response.status === 'success') {
                setProducts(response.data);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOrderPlaced = () => {
        console.log("handleOrderPlaced called!"); // DEBUG
        setCartItems([]);
        setIsCheckoutOpen(false);
        setIsCartOpen(false);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchProducts();
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery, activeCategory, selectedCity]);

    const currentRate = exchangeRates[currency] || 1;
    const cartTotalBase = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const cartTotalDisplay = (cartTotalBase * currentRate).toFixed(2);
    const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

    // Calculate max price dynamically
    const maxProductPrice = products.length > 0
        ? Math.max(...products.map(p => p.price * currentRate))
        : 5000;

    // Round up to nearest 100 for a cleaner slider max
    const maxSliderValue = Math.ceil(maxProductPrice / 100) * 100 || 5000;

    const currentMax = priceRange.max ? parseInt(priceRange.max) : maxSliderValue;
    const pricesPercent = (currentMax / maxSliderValue) * 100;

    return (
        <div className="marketplace-root">
            {/* --- Top Action Bar --- */}
            <div className="mp-action-bar">
                <div className="mp-action-bar-inner">
                    <div>
                        <h1 style={{ fontSize: '20px', fontWeight: '800', color: '#8548A8', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ShoppingBag size={24} />
                            Pet Marketplace
                        </h1>
                    </div>

                    <div className="mp-action-group">
                        {/* Currency Selector */}
                        <select
                            checked={false} // Prevent React warning, real value managed below
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="mp-currency-select"
                        >
                            <option value="SAR">🇸🇦 SAR</option>
                            {Object.keys(exchangeRates)
                                .filter(c => c !== 'SAR')
                                .sort()
                                .map(code => (
                                    <option key={code} value={code}>
                                        {code}
                                    </option>
                                ))}
                        </select>

                        <div style={{ width: '1px', height: '24px', background: '#E5E7EB', margin: '0 8px' }}></div>

                        {/* Wishlist Button */}
                        <button
                            className="mp-action-btn"
                            onClick={() => setIsWishlistOpen(true)}
                            title="My Wishlist"
                        >
                            <Heart size={20} />
                            {wishlistItems.length > 0 && <span className="mp-badge">{wishlistItems.length}</span>}
                        </button>

                        {/* History Button */}
                        <button
                            className="mp-action-btn"
                            onClick={() => setIsOrderHistoryOpen(true)}
                            title="Order History"
                        >
                            <Clock size={20} />
                        </button>

                        {/* Cart Button */}
                        <button
                            className="mp-action-btn"
                            onClick={() => setIsCartOpen(true)}
                            title="Your Cart"
                            style={{ background: cartItems.length > 0 ? '#F3E8FF' : 'white', borderColor: cartItems.length > 0 ? '#8548A8' : '#E5E7EB', color: cartItems.length > 0 ? '#8548A8' : 'inherit' }}
                        >
                            <ShoppingBag size={20} />
                            {cartCount > 0 && <span className="mp-badge">{cartCount}</span>}
                        </button>
                    </div>
                </div>
            </div>

            {/* --- Main Content Layout --- */}
            <div className="marketplace-container">
                <div className="marketplace-layout">

                    {/* Sidebar */}
                    <aside className="marketplace-sidebar">







                        <div className="mp-search-wrapper">
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="mp-search-input"
                            />
                            <Search className="mp-search-icon" size={20} />
                        </div>

                        {/* City Filter */}
                        <div className="mp-filters-section" style={{ marginBottom: '24px' }}>
                            <div className="mp-filter-title">
                                <MapPin size={14} style={{ display: 'inline', marginRight: '8px' }} />
                                City
                            </div>
                            <select
                                value={selectedCity}
                                onChange={(e) => setSelectedCity(e.target.value)}
                                className="mp-currency-select" // Reusing style
                                style={{ width: '100%', fontSize: '14px' }}
                            >
                                <option value="all">All Cities</option>
                                {availableCities.map(city => (
                                    <option key={city} value={city}>{city}</option>
                                ))}
                            </select>
                        </div>

                        {/* Sorting */}
                        <div className="mp-filters-section" style={{ marginBottom: '24px' }}>
                            <div className="mp-filter-title">
                                <SlidersHorizontal size={14} style={{ display: 'inline', marginRight: '8px' }} />
                                Sort By
                            </div>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="mp-currency-select" // Reusing this class for nicer look
                                style={{ width: '100%', fontSize: '14px' }}
                            >
                                <option value="newest">Newest Arrivals</option>
                                <option value="price-low">Price: Low to High</option>
                                <option value="price-high">Price: High to Low</option>
                                <option value="rating">Top Rated</option>
                            </select>
                        </div>

                        {/* Price Range */}
                        {/* Price Range Slider */}
                        <div className="mp-filters-section" style={{ marginBottom: '24px' }}>
                            <div className="mp-filter-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Max Price</span>
                                <span style={{ color: '#8548A8' }}>
                                    {priceRange.max ? `${parseInt(priceRange.max).toLocaleString()} ${currency}` : `${maxSliderValue.toLocaleString()} ${currency}`}
                                </span>
                            </div>

                            <div className="mp-range-slider">
                                <div
                                    className="mp-slider-track"
                                    style={{
                                        background: `linear-gradient(to right, #8548A8 ${pricesPercent}%, #E5E7EB ${pricesPercent}%)`
                                    }}
                                />
                                <input
                                    type="range"
                                    min="0"
                                    max={maxSliderValue}
                                    step="10"
                                    value={priceRange.max || maxSliderValue}
                                    onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                                    className="mp-range-input"
                                />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                                <span>0</span>
                                <span>{maxSliderValue.toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="mp-filters-section">
                            <div className="mp-filter-title">
                                <Filter size={14} style={{ display: 'inline', marginRight: '8px' }} />
                                Categories
                            </div>
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => setActiveCategory(cat.id)}
                                    className={`mp-category-item ${activeCategory === cat.id ? 'active' : ''}`}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    </aside>

                    {/* Product Grid Area */}
                    <main className="marketplace-content">
                        {loading ? (
                            <div className="marketplace-grid">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="mp-product-card" style={{ height: '380px', opacity: 0.5 }}>
                                        {/* Simple Skeleton */}
                                        <div style={{ background: '#eee', height: '250px' }}></div>
                                        <div style={{ padding: '20px' }}>
                                            <div style={{ height: '20px', background: '#eee', marginBottom: '10px' }}></div>
                                            <div style={{ height: '20px', background: '#eee', width: '60%' }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : products.length > 0 ? (
                            <div className="marketplace-grid">
                                {products
                                    .filter(product => {
                                        if (!priceRange.min && !priceRange.max) return true;

                                        // Calculate display price (the number the user sees)
                                        const displayPrice = product.price * currentRate;

                                        const min = priceRange.min ? parseFloat(priceRange.min) : 0;
                                        const max = priceRange.max ? parseFloat(priceRange.max) : Infinity;

                                        return displayPrice >= min && displayPrice <= max;
                                    })
                                    .sort((a, b) => {
                                        if (sortBy === 'newest') return new Date(b.date) - new Date(a.date);
                                        if (sortBy === 'price-low') return a.price - b.price;
                                        if (sortBy === 'price-high') return b.price - a.price;
                                        if (sortBy === 'rating') return b.rating - a.rating;
                                        return 0;
                                    })
                                    .map(product => (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            onAddToCart={onAddToCart}
                                            onViewDetails={(p) => setSelectedProduct(p)}
                                            onViewProvider={(id) => setSelectedProvider(id)}
                                            selectedCurrency={currency}
                                            rates={exchangeRates}
                                            isWishlisted={wishlistItems.some(item => item.id === product.id)}
                                            onToggleWishlist={toggleWishlist}
                                        />
                                    ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '80px 0', color: '#666' }}>
                                <Search size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                                <h3>No products found</h3>
                                <p>Try adjusting your search or filters.</p>
                                <button
                                    onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
                                    style={{ color: '#8548A8', border: 'none', background: 'none', cursor: 'pointer', marginTop: '10px', fontWeight: 'bold' }}
                                >
                                    Clear all filters
                                </button>
                            </div>
                        )}
                    </main>
                </div>
            </div>

            {/* --- Cart Sidebar --- */}
            <AnimatePresence>
                {isCartOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsCartOpen(false)}
                            className="mp-cart-backdrop"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="mp-cart-sidebar"
                        >
                            <div className="mp-cart-header">
                                <h2 style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <ShoppingBag style={{ color: '#8548A8' }} />
                                    Your Cart
                                </h2>
                                <button
                                    onClick={() => setIsCartOpen(false)}
                                    className="mp-cart-close"
                                >
                                    <X size={24} color="#666" />
                                </button>
                            </div>

                            <div className="mp-cart-items">
                                {cartItems.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                                        <ShoppingBag size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                                        <p>Your cart is empty.</p>
                                    </div>
                                ) : (
                                    cartItems.map(item => (
                                        <div key={item.id} className="mp-cart-item">
                                            <img src={item.image} alt={item.title} className="mp-cart-img" />
                                            <div className="mp-cart-info">
                                                <h4 style={{ fontWeight: '600', marginBottom: '4px' }}>{item.title}</h4>
                                                <p style={{ color: '#666', fontSize: '14px' }}>
                                                    {(item.price * currentRate).toFixed(2)} {currency}
                                                </p>

                                                <div className="mp-qty-control">
                                                    <button onClick={() => onUpdateQuantity(item.id, -1)} className="mp-qty-btn">-</button>
                                                    <span style={{ padding: '0 8px', fontSize: '14px' }}>{item.quantity}</span>
                                                    <button onClick={() => onUpdateQuantity(item.id, 1)} className="mp-qty-btn">+</button>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => onRemoveFromCart(item.id)}
                                                style={{ border: 'none', background: 'none', color: '#EF4444', cursor: 'pointer', height: 'fit-content' }}
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div className="mp-cart-footer">
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontWeight: 'bold' }}>
                                    <span>Subtotal</span>
                                    <span>{cartTotalDisplay} {currency}</span>
                                </div>
                                <button
                                    disabled={cartItems.length === 0}
                                    onClick={() => { setIsCartOpen(false); setIsCheckoutOpen(true); }}
                                    className="mp-checkout-btn"
                                >
                                    Checkout
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            <ProductDetailsModal
                product={selectedProduct}
                isOpen={!!selectedProduct}
                onClose={() => setSelectedProduct(null)}
                onAddToCart={onAddToCart}
                onViewProvider={(id) => { setSelectedProduct(null); setSelectedProvider(id); }}
                selectedCurrency={currency}
                rates={exchangeRates}
            />

            <WishlistModal
                isOpen={isWishlistOpen}
                onClose={() => setIsWishlistOpen(false)}
                wishlistItems={wishlistItems}
                onRemoveFromWishlist={removeFromWishlist}
                onAddToCart={onAddToCart}
                selectedCurrency={currency}
                rates={exchangeRates}
            />

            <OrderHistoryModal
                isOpen={isOrderHistoryOpen}
                onClose={() => setIsOrderHistoryOpen(false)}
                selectedCurrency={currency}
                rates={exchangeRates}
            />

            <CheckoutModal
                isOpen={isCheckoutOpen}
                onClose={() => setIsCheckoutOpen(false)}
                cartItems={cartItems}
                cartTotal={Number(cartTotalDisplay)}
                currency={currency}
                rate={currentRate}
                onPlaceOrder={handleOrderPlaced}
            />

            <ProviderStoreModal
                providerId={selectedProvider}
                isOpen={!!selectedProvider}
                onClose={() => setSelectedProvider(null)}
                allProducts={products}
                onAddToCart={onAddToCart}
                selectedCurrency={currency}
                rates={exchangeRates}
                onViewDetails={(p) => setSelectedProduct(p)}
            />
        </div>
    );
};

export default GuardianMarketplace;
